import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchFamilyRecapsForStudent } from '../../services/LessonRecapService';
import { groupRecapsByLesson, TIER_LABEL, RECAP_VISIBILITY } from '../../services/lessonRecapModel';
import RichTextView from '../../components/Portal/RichTextView';
import { Card, SectionHead, QuietState, studentDisplayName } from './customerUi';

/* Latest Lesson Recap — the Home card (Lesson Dashboard design pass 2, change
   11, 2026-09-21; card "Lesson recaps are reachable only from Schedule → Past
   lessons — give them a real front door").

   Until this, a recap was two deliberate actions from the front door (Schedule,
   then the Past lessons tab) and nothing else linked to it, while the recap
   email had started telling parents one existed. This card puts the newest one
   where a family already looks, and "All recaps" opens the full list.

   SAME READ AS THE LIST, SMALLER. One query per child through
   fetchFamilyRecapsForStudent, capped at 12 docs: the family tiers are at most
   two docs per lesson, and an untouched tier is a blank doc, so 12 reaches the
   newest lesson with something in it with room to spare. The tier wall is
   unchanged: the query asks only for the tiers this principal may read
   (studentOnly for a student account), groupRecapsByLesson drops the teacher
   tier a third time, and this file adds no filter of its own.

   READ-ONLY. Nothing here writes, and nothing should. */

const LATEST_READ_CAP = 12;

const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dateLabel = (d) => (d ? `${DOW[d.getDay()]}, ${MON[d.getMonth()]} ${d.getDate()}` : 'Last lesson');

const tierLabelStyle = {
	fontSize: '0.66rem',
	fontWeight: 700,
	letterSpacing: '0.08em',
	textTransform: 'uppercase',
	// teal-800: 5.42 / 6.59 / 6.97 on the card across light / Comfort / Comfort
	// Dark (teal-600 fails AA in two of them). Same token as StudentRecaps.
	color: 'var(--cyv-teal-800, #2a7570)',
	marginBottom: '0.3rem',
};

const LatestRecapCard = ({ students = [], studentOnly = false }) => {
	const navigate = useNavigate();
	// [{ student, group }] for every child with a recap, in the family's order.
	const [latest, setLatest] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const ids = students.map((s) => s.id).join('|');

	useEffect(() => {
		let cancelled = false;
		if (!students.length) {
			setLoading(false);
			return undefined;
		}
		(async () => {
			setLoading(true);
			setError(false);
			try {
				const results = await Promise.all(
					students.map(async (student) => {
						const rows = await fetchFamilyRecapsForStudent(student.id, LATEST_READ_CAP, { studentOnly });
						const [group] = groupRecapsByLesson(rows);
						return group ? { student, group } : null;
					})
				);
				if (!cancelled) setLatest(results.filter(Boolean));
			} catch (e) {
				// A failed load must not read as "no recaps yet" — that would tell a
				// parent the teacher wrote nothing. Same rule as StudentRecaps.
				console.error('Failed to load latest lesson recap', e);
				if (!cancelled) setError(true);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
		// `ids` stands in for `students` so a re-render with the same children
		// does not refetch.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ids, studentOnly]);

	if (!students.length) return null;

	const multiChild = students.length > 1;

	return (
		<Card style={{ marginBottom: 14 }}>
			<SectionHead
				icon='fa-comment-lines'
				label='Latest lesson recap'
				action='All recaps'
				onAction={() => navigate('/portal/customer/recaps')}
			/>
			{loading ? (
				<QuietState icon='fa-comment-lines'>Loading…</QuietState>
			) : error ? (
				<QuietState icon='fa-triangle-exclamation'>
					We couldn't load lesson recaps just now. Please try again in a moment.
				</QuietState>
			) : latest.length === 0 ? (
				<QuietState icon='fa-comment-lines'>
					{studentOnly
						? "After a lesson, your teacher's recap will show up here."
						: "After a lesson, your teacher's recap and any notes for parents will show up here."}
				</QuietState>
			) : (
				latest.map(({ student, group }, i) => {
					const multiTier = group.tiers.length > 1;
					return (
						<div
							key={student.id}
							style={{
								padding: '4px 15px 14px',
								borderTop: i > 0 ? '1px solid var(--cyv-line, #f0eae1)' : 'none',
								paddingTop: i > 0 ? 12 : 4,
								textAlign: 'left',
							}}>
							<div
								style={{
									fontSize: '0.9rem',
									fontWeight: 'var(--fw-bold, 700)',
									color: 'var(--text-heading, #384971)',
									marginBottom: '0.4rem',
								}}>
								{multiChild && `${studentDisplayName(student)} · `}
								{dateLabel(group.date)}
								{group.authorFirstName && (
									<span style={{ fontWeight: 400, color: 'var(--text-muted, #5f6873)' }}>
										{' '}· with {group.authorFirstName}
									</span>
								)}
							</div>
							<div style={{ fontSize: '0.93rem', color: 'var(--text-body, #3f3a34)' }}>
								{group.tiers.map((recap, t) => {
									const showLabel = multiTier || recap.visibility === RECAP_VISIBILITY.PARENT;
									return (
										<div key={recap.id} style={{ marginTop: t > 0 ? '0.7rem' : 0 }}>
											{showLabel && (
												<div style={tierLabelStyle}>{TIER_LABEL[recap.visibility] || 'Note'}</div>
											)}
											<RichTextView content={recap.content} fallback={recap.plainText} />
										</div>
									);
								})}
							</div>
						</div>
					);
				})
			)}
		</Card>
	);
};

export default LatestRecapCard;
